import { create } from "zustand";
import type { CoachingSessionState, UiInteractionSpec, InteractionSubmission } from "../types/state";
import type { TransportFrame, Lane } from "../types/transport";
import { appendAtPath, setAtPath } from "./_frameHelpers";

interface CoachingSessionStore {
  sessionState: CoachingSessionState | null;
  isStreaming: boolean;
  activeRequestId: string | null;
  error: { code: string; message: string } | null;

  processFrame: (frame: TransportFrame) => void;
  setActiveRequestId: (id: string | null) => void;
  /**
   * Write an optimistic submission onto an interaction (local-only).
   * The server echo (state_snapshot after freeze) overwrites this with
   * the canonical ``submission`` produced by the kind's
   * ``format_submission`` — including a real ``displayText`` and
   * no ``pending`` flag.  Keyed by ``eventId`` so the render/merge is
   * idempotent.
   */
  setPendingSubmission: (
    interactionId: string,
    partial: { eventId: string; displayText: string },
  ) => void;
  reset: () => void;
}

export const useCoachingSessionStore = create<CoachingSessionStore>(
  (set, get) => ({
    sessionState: null,
    isStreaming: false,
    activeRequestId: null,
    error: null,

    processFrame: (frame: TransportFrame) => {
      switch (frame.kind) {
        case "state_snapshot": {
          set({
            sessionState: frame.state,
            isStreaming: true,
            error: null,
          });
          break;
        }
        case "block_delta": {
          const state = get().sessionState;
          if (!state) break;
          const newState = structuredClone(state);
          applyBlockDelta(newState, frame.slotKey, frame.value, frame.lane);
          set({ sessionState: newState });
          break;
        }
        case "text_delta": {
          const state = get().sessionState;
          if (!state) break;
          const newState = structuredClone(state);
          applyTextDelta(newState, frame.slotKey, frame.delta, frame.lane);
          set({ sessionState: newState });
          break;
        }
        case "text_done": {
          break;
        }
        case "content_done": {
          set({ isStreaming: false });
          break;
        }
        case "run_done": {
          set({ activeRequestId: null, isStreaming: false });
          break;
        }
        case "error": {
          set({
            error: { code: frame.code, message: frame.message },
            isStreaming: false,
          });
          break;
        }
      }
    },

    setActiveRequestId: (id) => set({ activeRequestId: id }),

    setPendingSubmission: (interactionId, partial) => {
      const state = get().sessionState;
      if (!state) return;
      const newState = structuredClone(state);
      const scope = newState.scopes[newState.activeScopeId];
      const interaction = scope?.work?.interactions?.[interactionId];
      if (!interaction) return;
      const submission: InteractionSubmission = {
        ...(interaction.submission ?? {}),
        eventId: partial.eventId,
        displayText: partial.displayText,
        pending: true,
      };
      interaction.submission = submission;
      set({ sessionState: newState });
    },

    reset: () =>
      set({
        sessionState: null,
        isStreaming: false,
        activeRequestId: null,
        error: null,
      }),
  })
);

/** Return the active interaction (or null) on the active scope's chosen
 *  lane.  ``lane`` defaults to ``activity``; ``ask`` targets the help
 *  thread (Hint / Ask), whose coach reply streams independently of the
 *  learner's activity step. */
function getActiveInteraction(
  state: CoachingSessionState,
  lane: Lane = "work",
): UiInteractionSpec | null {
  const scope = state.scopes[state.activeScopeId];
  if (!scope) return null;
  const laneState = lane === "ask" ? scope.ask : scope.work;
  const id = laneState?.activeInteractionId;
  if (!id) return null;
  return (laneState.interactions[id] as UiInteractionSpec | undefined) ?? null;
}

/** The latest hint entry on the active activity step (lane="hint" target). */
function activeStepLastHint(
  state: CoachingSessionState,
): Record<string, unknown> | null {
  const step = getActiveInteraction(state, "work") as
    | (UiInteractionSpec & { hints?: Array<Record<string, unknown>> })
    | null;
  const hints = step?.hints;
  return hints && hints.length ? hints[hints.length - 1] : null;
}

/** Apply a complete block_delta to the active interaction on ``lane``. */
function applyBlockDelta(
  state: CoachingSessionState,
  slotKey: string,
  value: unknown,
  lane: Lane = "work",
): void {
  if (lane === "hint") {
    const hint = activeStepLastHint(state);
    if (hint) hint[slotKey] = value;
    return;
  }
  const interaction = getActiveInteraction(state, lane);
  if (!interaction) return;
  if (slotKey.includes(".")) {
    setAtPath(interaction as unknown as Record<string, unknown>, slotKey, value);
    return;
  }
  (interaction as unknown as Record<string, unknown>)[slotKey] = value;
}

/** Merge a text_delta into the session state for live rendering.
 *
 * Routing:
 * - ``lane === "hint"`` → the latest hint on the active activity step
 *   (its ``message`` slot), so hints stay anchored to the exercise.
 * - ``lane === "ask"`` → straight to the ask lane's active interaction
 *   (a coach_message); the overlay/activity routing below is skipped so
 *   Hint / Ask never disturb the learner's current step or an open wrap.
 * - Interaction-shaped overlay (e.g. session_wrap) → append to
 *   overlay[slotKey] so each declared stream field (summary, openLoop, …)
 *   fills correctly.
 * - Legacy term-preview overlay → append to overlay.description.
 * - Active activity interaction → append to interaction[slotKey] (dotted
 *   paths walk from interaction root).
 */
function applyTextDelta(
  state: CoachingSessionState,
  slotKey: string,
  delta: string,
  lane: Lane = "work",
): void {
  if (lane === "hint") {
    const hint = activeStepLastHint(state);
    if (hint) hint[slotKey] = ((hint[slotKey] as string | undefined) || "") + delta;
    return;
  }
  if (lane === "ask") {
    appendToInteraction(getActiveInteraction(state, "ask"), slotKey, delta);
    return;
  }

  if (state.overlay) {
    const o = state.overlay as unknown as Record<string, unknown>;
    if ("interactionId" in o) {
      if (slotKey.includes(".")) {
        appendAtPath(o, slotKey, delta);
      } else {
        o[slotKey] = ((o[slotKey] as string | undefined) || "") + delta;
      }
      return;
    }
    if ("description" in o) {
      o.description = ((o.description as string | undefined) || "") + delta;
      return;
    }
  }

  appendToInteraction(getActiveInteraction(state, "work"), slotKey, delta);
}

/** Append a text delta into an interaction's slot (dotted paths walk from
 *  the interaction root). */
function appendToInteraction(
  interaction: UiInteractionSpec | null,
  slotKey: string,
  delta: string,
): void {
  if (!interaction) return;
  if (slotKey.includes(".")) {
    appendAtPath(interaction as unknown as Record<string, unknown>, slotKey, delta);
    return;
  }
  const rec = interaction as unknown as Record<string, unknown>;
  rec[slotKey] = ((rec[slotKey] as string | undefined) || "") + delta;
}

// Convenience selectors
export const useCoachingSessionState = () =>
  useCoachingSessionStore((s) => s.sessionState);
export const useIsStreaming = () =>
  useCoachingSessionStore((s) => s.isStreaming);
export const useCoachingError = () =>
  useCoachingSessionStore((s) => s.error);

// -- Shared pure helpers (used by the page + lane + help UI) ---------- //

/** Count of activity steps that "count" toward progress — i.e. NOT
 *  superseded by a regeneration.  A "feels too easy / hard" re-do is
 *  net-zero: the old version becomes superseded, a new one takes its
 *  place, so the session/phase/wrap boundary doesn't advance. */
export function nonSupersededCount(
  order: string[],
  interactions: Record<string, UiInteractionSpec>,
): number {
  return order.reduce(
    (n, id) => (interactions[id]?.status === "superseded" ? n : n + 1),
    0,
  );
}
